import axiosInstance from "../axiosInstance/axiosInstance";

// Every photo costs two round trips: one to turn an S3 key into a download URL,
// then the download itself. Nothing paints until both land, which is why a
// screen shows its placeholder first. Holding on to the URL lets repeat views
// skip the first trip, and the second one comes out of the browser's own cache
// because the URL it is keyed on stays the same.
const CACHE_DURATION = 2 * 60 * 60 * 1000;

const memoryCache = new Map();

const sessionKey = (key) => `hotel-image-${key}`;

const readSession = (key) => {
    try {
        const stored = sessionStorage.getItem(sessionKey(key));

        if (!stored) {
            return null;
        }

        const parsed = JSON.parse(stored);

        if (parsed.url && Date.now() - parsed.timestamp < CACHE_DURATION) {
            return parsed;
        }

        sessionStorage.removeItem(sessionKey(key));
    } catch (error) {
        // Unreadable or unavailable storage is just a cache miss.
    }

    return null;
};

const writeSession = (key, entry) => {
    try {
        sessionStorage.setItem(sessionKey(key), JSON.stringify(entry));
    } catch (error) {
        // Full or disabled storage is not worth failing the image over.
    }
};

// The URL if it is already known, without touching the network. Screens use
// this for their initial state: an effect cannot run before the first paint, so
// resolving the URL only in an effect guarantees the placeholder is shown first
// even when the real URL was already in hand.
export const peekImageUrl = (key) => {
    if (!key) {
        return null;
    }

    const cached = memoryCache.get(key);

    if (cached && !(cached instanceof Promise)) {
        if (cached.url && Date.now() - cached.timestamp < CACHE_DURATION) {
            return cached.url;
        }

        memoryCache.delete(key);
    }

    const stored = readSession(key);

    if (stored) {
        memoryCache.set(key, stored);
        return stored.url;
    }

    return null;
};

export const getImageUrl = async (key) => {
    if (!key) {
        return "";
    }

    const cached = peekImageUrl(key);

    if (cached) {
        return cached;
    }

    // Several cards can want the same hotel photo at once, and StrictMode runs
    // each of their effects twice. They all wait on the one request.
    const inFlight = memoryCache.get(key);

    if (inFlight instanceof Promise) {
        return inFlight;
    }

    const request = (async () => {
        try {
            const response = await axiosInstance.get(
                `/api/v1/images/download-presigned-url?key=${encodeURIComponent(key)}`
            );

            const url = response.data.url;
            const entry = { url, timestamp: Date.now() };

            memoryCache.set(key, entry);
            writeSession(key, entry);

            return url;
        } catch (error) {
            memoryCache.delete(key);
            throw error;
        }
    })();

    memoryCache.set(key, request);

    return request;
};

// Warms the URL and the image bytes for a screen that is one tap away, so it
// paints from cache instead of from its placeholder.
export const prefetchImage = async (key) => {
    if (!key || peekImageUrl(key)) {
        return;
    }

    try {
        const url = await getImageUrl(key);

        if (url) {
            const image = new Image();
            image.src = url;
        }
    } catch (error) {
        // A failed prefetch costs nothing; the screen fetches it itself.
    }
};
